import { useMemo, useState } from 'react';
import { slotsFor, upcomingOpenDays } from '../core/rules/schedule';
import { freeTimes } from '../core/rules/agenda';
import { dayMedium, fromIso, iso, longDate, weekdayShort } from '../core/format/dates';
import type { OpeningDay } from '../core/types/site';
import type { AvailabilityDay } from '../core/types/agenda';
import { Spinner } from './Spinner';
import { btnPrimary, btnQuiet } from './styles';
type Props = {
  /** De openingstijden waarbinnen gekozen mag worden: live op de site, het concept in het beheer. */
  hours: OpeningDay[];
  /**
   * De écht vrije tijden van de server (`GET /availability`). Gegeven: alleen die tijden, en
   * dagen zonder vrije tijd vallen weg. `null` = nog aan het laden. Weggelaten: alleen de
   * openingstijden (het beheer, waar Sarah bewust overal mag plannen).
   */
  available?: AvailabilityDay[] | null;
  /**
   * Duur van de behandeling. Gegeven en `available` ontbreekt (ophalen mislukt): de terugval rekent
   * met dezelfde regel als de server (`freeTimes`): alleen starttijden waarbij de behandeling vóór
   * sluiten klaar is. Afspraken en blokkades kent de terugval niet; die weigert de server.
   */
  durationMinutes?: number;
  onConfirm: (date: string, time: string) => void;
  onCancel: () => void;
  /** Tekst op de knop, met de gekozen dag en tijd: "Verzet naar do 8 okt 13:00". */
  confirmLabel?: (when: string) => string;
  cancelLabel?: string;
  /** Hoeveel open dagen er te kiezen zijn. */
  dayCount?: number;
  /** Verzoek onderweg: bevestigen staat uit en zegt dat, zodat niemand twee keer tikt. */
  busy?: boolean;
};

/** Dag en tijd kiezen — binnen de vrije tijden als die er zijn, anders binnen de openingstijden. */
export function SlotPicker({
  hours,
  available,
  durationMinutes,
  onConfirm,
  onCancel,
  confirmLabel = when => `Verzet naar ${when}`,
  cancelLabel = 'Laat staan',
  dayCount = 8,
  busy = false
}: Props) {
  const days = useMemo(() => available ? available.filter(d => d.times.length > 0).map(d => fromIso(d.date)) : upcomingOpenDays(hours, dayCount), [available, hours, dayCount]);
  // Op datum bewaren: na verversen van de vrije tijden kan een volle dag wegvallen.
  const [picked, setPicked] = useState<string | null>(null);
  const [time, setTime] = useState('');
  const day = days.find(d => iso(d) === picked) ?? days[0];
  const slots = !day ? [] : available ? available.find(d => d.date === iso(day))?.times ?? [] : durationMinutes ? freeTimes(hours, iso(day), durationMinutes, [], []) : slotsFor(hours, day);
  const chosen = slots.includes(time) ? time : '';
  if (available === null) {
    return <div className="flex items-center gap-2 bg-sand p-4 text-[14px] text-muted sm:p-5" role="status" data-id="element-8869" data-mp="2ccbt83ts9poo">
        <Spinner data-id="element-8870" data-mp="27ds9ma2nwpzh" /> Vrije tijden ophalen…
      </div>;
  }
  if (days.length === 0) {
    return <div className="bg-sand p-4 sm:p-5" data-id="element-8871" data-mp="vw8vvnrgh4mo">
        <p className="text-[14px] text-ink" data-id="element-8872" data-mp="vv7ivdqvq82b">{available ? 'De komende weken is er geen tijd meer vrij.' : 'Er zijn de komende weken geen open dagen.'}</p>
        <button type="button" onClick={onCancel} className={`${btnQuiet} mt-3`} data-id="element-8873" data-mp="qreut89gdbts">
          {cancelLabel}
        </button>
      </div>;
  }
  return <div className="bg-sand p-4 sm:p-5" data-id="element-8874" data-mp="vpq67nfebue4">
      <p className="text-[14px] text-ink" id="slot-day-label" data-id="element-8875" data-mp="vonifodde82f">
        Kies een dag
      </p>
      <div role="group" aria-labelledby="slot-day-label" className="-mx-1 mt-3 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none]" data-id="element-8876" data-mp="10j1g5xfi8ra9">
        {days.map(d => {
        const on = Boolean(day && iso(d) === iso(day));
        return <button key={iso(d)} type="button" aria-pressed={on} aria-label={longDate(d)} onClick={() => {
          setPicked(iso(d));
          setTime('');
        }} className={`flex w-14 shrink-0 flex-col items-center rounded-full py-2 transition-colors duration-150 ease-soft ${on ? 'bg-ink text-shell' : 'bg-shell text-ink ring-1 ring-line hover:ring-ink/40'}`} data-id="element-8877" data-mp="1ei15l7auda7h">
              <span className="text-[11px] capitalize" aria-hidden="true" data-id="element-8878" data-mp="1ajk92m6kngik">
                {weekdayShort(d)}
              </span>
              <span className="font-display text-[18px] leading-tight" aria-hidden="true" data-id="element-8879" data-mp="1ohh9xus26931">
                {d.getDate()}
              </span>
            </button>;
      })}
      </div>

      <p className="mt-4 text-[14px] text-ink" id="slot-time-label" data-id="element-8880" data-mp="vt36gzi7akk3">
        Tijd
      </p>
      {slots.length === 0 ? <p className="mt-2 text-[14px] text-muted" data-id="element-8881" data-mp="15kpi2wygalnd">Geen tijden op deze dag.</p> : <div role="group" aria-labelledby="slot-time-label" className="mt-2 flex flex-wrap gap-1.5" data-id="element-8882" data-mp="10u95tg6d962l">
          {slots.map(s => <button key={s} type="button" aria-pressed={chosen === s} onClick={() => setTime(s)} className={`rounded-full px-4 py-2 text-[14px] tabular-nums transition-colors duration-150 ease-soft ${chosen === s ? 'bg-ink text-shell' : 'bg-shell text-ink ring-1 ring-line hover:ring-ink/40'}`} data-id="element-8883" data-mp="d3mvfi8w39pf">
              {s}
            </button>)}
        </div>}

      <div className="mt-5 flex flex-wrap items-center gap-2" data-id="element-8884" data-mp="2fqtxlfp48bx9">
        <button type="button" disabled={busy || !day || !chosen} aria-busy={busy} onClick={() => day && onConfirm(iso(day), chosen)} className={btnPrimary} data-id="element-8885" data-mp="2fp8xlak180s0">
          {busy ? <Spinner data-id="element-8886" data-mp="37h86exi9uy22" /> : null}
          {busy ? 'Bezig…' : day && chosen ? confirmLabel(`${dayMedium(iso(day))} ${chosen}`) : 'Kies een dag en tijd'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={btnQuiet} data-id="element-8887" data-mp="2yhbl95v3chbr">
          {cancelLabel}
        </button>
      </div>
    </div>;
}